import { action, computed, makeObservable, observable } from 'mobx';
import { persist } from 'mobx-persist';
import afterUserHydration from '../afterUserHydration';
import { branchStore } from '../branchStore';
import { callCenterStore } from '../ordersStore/callCenterStore';
import { liveOrdersStore } from '../ordersStore/liveOrdersStore';
import { ordersHistoryStore } from '../ordersHistoryStore/ordersHistoryStore';
import { USER_ROLES } from 'src/constants/user';
import { WMS_COMMANDS } from 'src/constants/wms';
import { requests } from 'src/requests';
import client from 'src/services/client';
import cookies from 'src/services/cookie';
import sentry from 'src/services/sentry';
import wms from 'src/services/wms';
import { IUser, IUserBranch } from 'src/types/user';
import { BranchAccessMessage } from 'src/view/containers/Messages';
import { SCREENS } from '~/constants/screens';
import logger from '~/services/logger';
import { messagesStore } from '~/view/containers/Messages';
import { itemsInventoryProductStore } from '~/view/screens/ItemsInventoryScreen/screens/ProductsScreen/store/ItemsInventoryProductStore';
import { itemsInventoryDealStore } from '~/view/screens/ItemsInventoryScreen/screens/DealCombosScreen/store/itemsInventoryDealStore';
import { itemsInventoryMealStore } from '~/view/screens/ItemsInventoryScreen/screens/MealCombosScreen/store/itemsInventoryMealStore';
import { IBranchForSelect } from '~/view/screens/ReportsScreen/components/ReportsFranchiseePicker/ReportsFranchiseePicker';

export class UserStore {
  constructor() {
    makeObservable(this);
  }

  public get persist() {
    return true;
  }

  private initialUserObject: IUser = {
    id: 0,
    name: 'User',
    email: '',
    auth_token: '',
    jwt_token: '',
    branches: [],
    deleted_branches: [],
    role: '',
    permissions: [],
    service_user: false,
    hint_messages: {
      change_profile_branch_dispatching_rules: 0,
      draw_polygon: 0,
    },
  };

  @persist @observable public isLogin: boolean = false;
  @persist @observable public firebase_token: string = '';
  @persist('object') @observable public user: IUser = this.initialUserObject;
  // actions

  @computed get allUserBranches(): IUserBranch[] {
    return [...this.user.branches, ...this.user.deleted_branches];
  }

  @action public authorize = async (user: IUser, firebase_token?: string, redirectUrl?: string) => {
    if (firebase_token) this.firebase_token = firebase_token;
    this.user = user;
    // provide sentry user context
    sentry.provideContext.user(user);
    if (user.auth_token) {
      await afterUserHydration(this.user);
    }
    // Use private router after hydration
    this.isLogin = true;

    branchStore.updateBranchesFilters();

    if (redirectUrl && redirectUrl.startsWith(window.location.origin) && window.location.pathname !== SCREENS.LOGIN()) {
      window.location.href = redirectUrl;
    }
  };

  @action private fetchUser = async () => {
    try {
      const user: IUser = await requests.fetchUser();
      this.user = { ...this.user, ...user };

      // provide sentry user context
      sentry.provideContext.user(this.user);
      branchStore.updateBranchesFilters();
      this.branchesAvailable(user);
    } catch (error: any) {
      logger.error({ error, place: 'UserStore fetchUser' });
    }
  };

  @action public login = async (data: { username: string; password: string; firebase_token?: string }) => {
    try {
      const user: IUser = await client.post(`/v3/auth/login`, { ...data, token: data.firebase_token });

      if (!this.branchesAvailable(user)) return;

      const isNewAdmin = cookies.getCookie('is_new_admin');

      if (!window.IS_MOBILE && process.env.NODE_ENV !== 'development') {
        cookies.deleteCookie('is_new_admin');

        const notRedirectedUserRoles = [
          USER_ROLES.CASHIER,
          USER_ROLES.MARKETING,
          USER_ROLES.FRANCHISEE,
          USER_ROLES.CALL_CENTER_AGENT,
          USER_ROLES.ACCOUNTING,
          USER_ROLES.RESTAURANT_MANAGER,
          USER_ROLES.ACCOUNTING,
          USER_ROLES.CUSOMER_CARE,
          USER_ROLES.OPERATOR,
        ];

        // redirect any other roles to old admin panel
        if (isNewAdmin === '0' || !notRedirectedUserRoles.includes(user?.role)) {
          window.location.href = '/admin/dashboard';
          return;
        }
      }

      let redirectUrl = '';
      try {
        redirectUrl =
          JSON.parse('{"' + window.location.search.substring(1).replace(/&/g, '","').replace(/=/g, '":"') + '"}', (key, value) =>
            key === '' ? value : decodeURIComponent(value)
          )?.redirect_to || '';
      } catch (error) {
        //
      }

      this.authorize(user, data.firebase_token, redirectUrl);
    } catch (error: any) {
      wms.setLoading(false);
      throw error;
    }
  };

  @action public logout = () => {
    try {
      wms.setLoading(true);
      this.isLogin = false;
      this.firebase_token = '';
      client.post('/v3/auth/logout');
      wms.actions[WMS_COMMANDS.LOGOUT]();
      this.user = this.initialUserObject;

      this.clearStores();

      cookies.deleteAllCookies();
      client.headers = {
        Authorization: ``,
      };

      if (window.IS_MOBILE) {
        // Clear token after logout
        this.sendFirebaseToken();
        window.location.href = window.DEFAULT_DOMAIN;
      }
      wms.setLoading(false);
    } catch (error: any) {
      wms.setLoading(false);
      logger.error({ error, place: 'UserStore logout' });
    }
  };

  @action getAllBranches = async (): Promise<IBranchForSelect[]> => {
    if (!this?.allUserBranches) {
      return [];
    }

    const allUserBranches = this?.allUserBranches.map((branch: IUserBranch): IBranchForSelect => {
      const language = Object.keys(branch.name)[0];
      const branch_name = branch.name[language];

      return {
        id: branch.id,
        description: { branch_name, language },
        is_delete: branch.is_delete,
      };
    });

    return allUserBranches;
  };

  @action public resetPassword = async (data: { email: string; token: string }) => {
    try {
      const response = await client.post('/v3/auth/reset_password', data);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  @action public sendFirebaseToken = async (): Promise<void> => {
    if (window.IS_MOBILE) {
      const token = await wms.getFirebaseToken();
      if (token) {
        await client.post(`/v3/auth/push_token`, {
          firebase_token: token,
          user_id: this.user?.id || 0,
        });
      }
    }
  };

  public branchesAvailable = (user?: IUser) => {
    if ((user && user.role === USER_ROLES.RESTAURANT_MANAGER) || (!user && this.user && this.user.role === USER_ROLES.RESTAURANT_MANAGER)) {
      return true;
    }

    if (user ? !user.branches.length : !this.user.branches.length) {
      messagesStore.addMessage({
        hideCloseIcon: true,
        Content: BranchAccessMessage,
        contentClassName: 'branch-access-modal',
      });
      return false;
    }
    // if there are no branches, then false
    return true;
  };

  public hintMessageUpdate = (hint: Partial<IUser['hint_messages']>) => {
    this.user.hint_messages = this.user.hint_messages && hint ? { ...this.user.hint_messages, ...hint } : { ...hint };
  };

  private clearStores = () => {
    // items inventory
    itemsInventoryProductStore.clear();
    itemsInventoryDealStore.clear();
    itemsInventoryMealStore.clear();
    // orders
    callCenterStore.clear();
    liveOrdersStore.clear();
    ordersHistoryStore.clear();
  };

  public updateUser = async () => {
    try {
      const user: IUser = await requests.fetchUser();
      this.user = { ...this.user, ...user };
    } catch (error) {
      throw error;
    }
  };

  public async afterHydration() {
    try {
      if (this.isLogin && this.user.auth_token) {
        await afterUserHydration(this.user);

        sentry.provideContext.user(this.user);
        this.fetchUser();
        this.sendFirebaseToken();
      } else if (this.isLogin) {
        this.logout();
      }
    } catch (error: any) {
      logger.error({ error, place: 'UserStore afterHydration' });
    }
  }
}

export const userStore = new UserStore();
